import { ArrowClockwiseIcon } from '@phosphor-icons/react/ArrowClockwise'
import { GameControllerIcon } from '@phosphor-icons/react/GameController'
import { LightningIcon } from '@phosphor-icons/react/Lightning'
import { PauseIcon } from '@phosphor-icons/react/Pause'
import { PlayIcon } from '@phosphor-icons/react/Play'
import { SpeakerHighIcon } from '@phosphor-icons/react/SpeakerHigh'
import { SpeakerSlashIcon } from '@phosphor-icons/react/SpeakerSlash'
import { useEffect, useMemo, useReducer, useRef, useState, type CSSProperties } from 'react'
import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  cellsFor,
  createInitialGameState,
  createTetrisGameReducer,
  loadHighScore,
  piecePowers,
  tetrisActions,
  type GameMode,
  type PieceType,
} from '../../../game/tetrisEngine'
import './tetris.css'
import { tetrisAudio } from './tetrisAudio'

const PREVIEW_SHAPES: Record<PieceType, number[][]> = {
  I: [[1, 1, 1, 1]],
  O: [[1, 1], [1, 1]],
  T: [[0, 1, 0], [1, 1, 1]],
  S: [[0, 1, 1], [1, 1, 0]],
  Z: [[1, 1, 0], [0, 1, 1]],
  J: [[1, 0, 0], [1, 1, 1]],
  L: [[0, 0, 1], [1, 1, 1]],
}

const PIECE_COLORS: Record<PieceType, string> = {
  I: '#5ee7ff',
  O: '#ffd166',
  T: '#c084fc',
  S: '#62e6a7',
  Z: '#ff718d',
  J: '#6f9cff',
  L: '#ff9f5a',
}

function MiniPiece({ type }: { type: PieceType }) {
  const shape = PREVIEW_SHAPES[type]
  const color = PIECE_COLORS[type]
  const cols = shape[0]?.length ?? 0
  const rows = shape.length

  return (
    <div
      className="tetris-mini-grid"
      style={{
        gridTemplateColumns: `repeat(${cols}, 14px)`,
        gridTemplateRows: `repeat(${rows}, 14px)`,
      }}
      aria-label={`${type} tetromino preview`}
    >
      {shape.map((row, r) =>
        row.map((val, c) => (
          <div
            key={`${r}-${c}`}
            className={`tetris-mini-cell ${val ? `piece-${type}` : 'is-empty'}`}
            style={val ? { background: color, boxShadow: `0 0 6px ${color}88` } : undefined}
          />
        ))
      )}
    </div>
  )
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSec / 60)
  const seconds = totalSec % 60
  const tenths = Math.floor((ms % 1000) / 100)
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${tenths}`
}

export function TetrisView() {
  const [mode, setMode] = useState<GameMode>('endless')
  const [muted, setMuted] = useState(() => tetrisAudio.isMuted())
  const [powerBanner, setPowerBanner] = useState<{ text: string; color: string } | null>(null)
  const [effectCells, setEffectCells] = useState<Set<string>>(new Set())
  const [touchControlsVisible, setTouchControlsVisible] = useState(true)

  const reducer = useMemo(() => createTetrisGameReducer({ mode }), [mode])
  const [state, dispatch] = useReducer(reducer, undefined, () => createInitialGameState({ mode }))

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Track touch / swipe gestures on the matrix
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)

  // Switch mode
  const handleSelectMode = (newMode: GameMode) => {
    setMode(newMode)
    dispatch(tetrisActions.restart(newMode))
    dispatch(tetrisActions.setHighScore(loadHighScore(newMode)))
  }

  // Audio mute toggle
  const handleToggleMute = () => {
    const next = tetrisAudio.toggleMute()
    setMuted(next)
  }

  // Fall interval / game loop tick
  useEffect(() => {
    if (state.status !== 'playing') return
    const interval = setInterval(() => {
      dispatch(tetrisActions.tick())
    }, state.dropInterval)
    return () => clearInterval(interval)
  }, [state.status, state.dropInterval])

  // Smooth millisecond timer for Sprint and Power Run
  useEffect(() => {
    if (state.status !== 'playing') return
    const interval = setInterval(() => {
      dispatch(tetrisActions.tick(100))
    }, 100)
    return () => clearInterval(interval)
  }, [state.status])

  // Reaction to state changes (sounds, power banners, effect flashes)
  const lastPlacedCountRef = useRef(state.stats.piecesPlaced)
  const lastStatusRef = useRef(state.status)

  useEffect(() => {
    // Sound & banner for lock results
    if (state.stats.piecesPlaced > lastPlacedCountRef.current) {
      lastPlacedCountRef.current = state.stats.piecesPlaced
      const lockResult = state.lastLockResult
      if (lockResult) {
        if (lockResult.clearedLines > 0) {
          tetrisAudio.playLineClear(lockResult.clearedLines)
        }
        if (lockResult.effectCells.length > 0) {
          tetrisAudio.playPower()
          const power = piecePowers[lockResult.power]
          setPowerBanner({
            text: `⚡ ${power.name.toUpperCase()}! ${lockResult.effectCells.length} BLOCKS REMOVED`,
            color: power.color,
          })
          const cellKeys = new Set(lockResult.effectCells.map(([x, y]) => `${x},${y}`))
          setEffectCells(cellKeys)

          setTimeout(() => {
            setEffectCells(new Set())
          }, 600)

          setTimeout(() => {
            setPowerBanner(null)
          }, 2400)
        }
      }
    }

    // Status changes
    if (state.status !== lastStatusRef.current) {
      if (state.status === 'game-over') {
        tetrisAudio.playGameOver()
      } else if (state.status === 'won') {
        tetrisAudio.playVictory()
      }
      lastStatusRef.current = state.status
    }
  }, [state.stats.piecesPlaced, state.status, state.lastLockResult])

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      const currentState = stateRef.current

      switch (e.code) {
        case 'ArrowLeft':
        case 'KeyA':
          e.preventDefault()
          dispatch(tetrisActions.moveLeft())
          tetrisAudio.playMove()
          break
        case 'ArrowRight':
        case 'KeyD':
          e.preventDefault()
          dispatch(tetrisActions.moveRight())
          tetrisAudio.playMove()
          break
        case 'ArrowUp':
        case 'KeyW':
        case 'KeyX':
          e.preventDefault()
          dispatch(tetrisActions.rotateCW())
          tetrisAudio.playRotate()
          break
        case 'KeyZ':
          e.preventDefault()
          dispatch(tetrisActions.rotateCCW())
          tetrisAudio.playRotate()
          break
        case 'ArrowDown':
        case 'KeyS':
          e.preventDefault()
          dispatch(tetrisActions.softDrop())
          tetrisAudio.playSoftDrop()
          break
        case 'Space':
          e.preventDefault()
          dispatch(tetrisActions.hardDrop())
          tetrisAudio.playHardDrop()
          break
        case 'KeyC':
        case 'ShiftLeft':
        case 'ShiftRight':
          e.preventDefault()
          if (currentState.canHold) {
            dispatch(tetrisActions.hold())
            tetrisAudio.playHold()
          }
          break
        case 'KeyP':
        case 'Escape':
          e.preventDefault()
          dispatch(tetrisActions.togglePause())
          break
        case 'KeyR':
          e.preventDefault()
          dispatch(tetrisActions.restart(currentState.mode))
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Touch handlers for swipe controls
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    if (!touch) return
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return
    const touch = e.changedTouches[0]
    if (!touch) return

    const dx = touch.clientX - touchStartRef.current.x
    const dy = touch.clientY - touchStartRef.current.y
    const dt = Date.now() - touchStartRef.current.time
    touchStartRef.current = null

    const dist = Math.hypot(dx, dy)

    if (dist < 12 && dt < 250) {
      // Tap -> Rotate CW
      dispatch(tetrisActions.rotateCW())
      tetrisAudio.playRotate()
      return
    }

    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal swipe
      if (dx > 25) {
        dispatch(tetrisActions.moveRight())
        tetrisAudio.playMove()
      } else if (dx < -25) {
        dispatch(tetrisActions.moveLeft())
        tetrisAudio.playMove()
      }
    } else {
      // Vertical swipe
      if (dy > 80 && dt < 280) {
        // Fast swipe down -> Hard drop
        dispatch(tetrisActions.hardDrop())
        tetrisAudio.playHardDrop()
      } else if (dy > 25) {
        // Soft drop
        dispatch(tetrisActions.softDrop())
        tetrisAudio.playSoftDrop()
      } else if (dy < -30) {
        // Swipe up -> Rotate CCW
        dispatch(tetrisActions.rotateCCW())
        tetrisAudio.playRotate()
      }
    }
  }

  // Pre-calculate active piece and ghost cells for the 10x20 grid
  const { activeCellsMap, ghostCellsSet } = useMemo(() => {
    const activeMap = new Map<string, PieceType>()
    if (state.activePiece) {
      cellsFor(state.activePiece).forEach(([x, y]) => {
        if (x >= 0 && x < BOARD_WIDTH && y >= 0 && y < BOARD_HEIGHT) {
          activeMap.set(`${x},${y}`, state.activePiece!.type)
        }
      })
    }

    const ghostSet = new Set<string>()
    if (state.ghostPiece && state.status === 'playing') {
      cellsFor(state.ghostPiece).forEach(([x, y]) => {
        if (x >= 0 && x < BOARD_WIDTH && y >= 0 && y < BOARD_HEIGHT) {
          ghostSet.add(`${x},${y}`)
        }
      })
    }

    return { activeCellsMap: activeMap, ghostCellsSet: ghostSet }
  }, [state.activePiece, state.ghostPiece, state.status])

  const activePower = state.activePiece ? piecePowers[state.activePiece.type] : null

  // Progress metrics calculation
  const goalProgress = useMemo(() => {
    if (state.mode === 'sprint') {
      const target = 40
      const current = Math.min(target, state.lines)
      const percent = Math.min(100, Math.round((current / target) * 100))
      return { current, target, label: `${current} / ${target} lines`, percent }
    }
    if (state.mode === 'power-run') {
      const target = 25000
      const current = Math.min(target, state.score)
      const percent = Math.min(100, Math.round((current / target) * 100))
      return { current, target, label: `${current.toLocaleString()} / 25,000 pts`, percent }
    }
    // Endless
    const toNextLevel = 10 - (state.lines % 10)
    const percent = Math.round(((10 - toNextLevel) / 10) * 100)
    return { current: state.lines, target: null, label: `${toNextLevel} to next level`, percent }
  }, [state.mode, state.lines, state.score])

  return (
    <div className="tetris-root" role="region" aria-label="Modern Tetris Game">
      {/* Top Bar Navigation & Controls */}
      <header className="tetris-topbar">
        <div className="tetris-brand">
          <span className="tetris-badge">
            <GameControllerIcon size={14} weight="bold" /> Overdrive
          </span>
          <h2 className="tetris-title">Neon Tetris</h2>
        </div>

        {/* Mode Selector */}
        <nav className="tetris-mode-tabs" aria-label="Oyun Modları">
          <button
            type="button"
            className={`tetris-mode-tab ${state.mode === 'endless' ? 'is-active' : ''}`}
            onClick={() => handleSelectMode('endless')}
          >
            Endless
          </button>
          <button
            type="button"
            className={`tetris-mode-tab ${state.mode === 'sprint' ? 'is-active' : ''}`}
            onClick={() => handleSelectMode('sprint')}
          >
            Sprint 40
          </button>
          <button
            type="button"
            className={`tetris-mode-tab ${state.mode === 'power-run' ? 'is-active' : ''}`}
            onClick={() => handleSelectMode('power-run')}
          >
            Power Run
          </button>
        </nav>

        {/* Game State & Audio Actions */}
        <div className="tetris-actions-bar">
          <button
            type="button"
            className={`tetris-icon-button ${muted ? '' : 'is-active'}`}
            onClick={handleToggleMute}
            aria-label={muted ? 'Unmute game audio' : 'Mute game audio'}
            title={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <SpeakerSlashIcon size={16} /> : <SpeakerHighIcon size={16} />}
            <span>{muted ? 'Muted' : 'Sound'}</span>
          </button>

          <button
            type="button"
            className="tetris-icon-button"
            onClick={() => dispatch(tetrisActions.togglePause())}
            aria-label={state.status === 'paused' ? 'Resume game' : 'Pause game'}
          >
            {state.status === 'paused' ? <PlayIcon size={16} weight="fill" /> : <PauseIcon size={16} weight="fill" />}
            <span>{state.status === 'paused' ? 'Resume' : 'Pause'}</span>
          </button>

          <button
            type="button"
            className="tetris-icon-button"
            onClick={() => dispatch(tetrisActions.restart(state.mode))}
            aria-label="Restart game"
          >
            <ArrowClockwiseIcon size={16} />
            <span>Restart</span>
          </button>

          <button
            type="button"
            className={`tetris-icon-button ${touchControlsVisible ? 'is-active' : ''}`}
            onClick={() => setTouchControlsVisible((v) => !v)}
            aria-label="Toggle touch buttons"
            title="Toggle touch controls"
          >
            <span>Touch</span>
          </button>
        </div>
      </header>

      {/* Main Playing Arena */}
      <div className="tetris-arena">
        {/* Left Panel: Hold slot & Active Piece Power */}
        <aside className="tetris-panel">
          {/* Hold Slot */}
          <section className="tetris-card">
            <div className="tetris-card-header">
              <h3 className="tetris-card-title">Hold Piece</h3>
              <span className="tetris-card-tag">C / ⇧</span>
            </div>
            <div
              className={`tetris-hold-box ${state.canHold ? '' : 'is-locked'}`}
              onClick={() => {
                if (state.canHold && state.status === 'playing') {
                  dispatch(tetrisActions.hold())
                  tetrisAudio.playHold()
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  if (state.canHold) dispatch(tetrisActions.hold())
                }
              }}
              aria-label={state.heldPiece ? `Held piece: ${state.heldPiece}` : 'Hold slot empty'}
            >
              {state.heldPiece ? (
                <MiniPiece type={state.heldPiece} />
              ) : (
                <span style={{ fontSize: 12, color: '#556882' }}>Empty</span>
              )}
              <span className={`tetris-hold-status ${state.canHold ? 'is-ready' : 'is-locked'}`}>
                {state.canHold ? 'Ready' : 'Locked'}
              </span>
            </div>
          </section>

          {/* Active Piece Landing Power Card */}
          <section
            className="tetris-card tetris-power-card"
            style={activePower ? ({ '--power-color': activePower.color } as CSSProperties) : undefined}
          >
            <div className="tetris-card-header">
              <h3 className="tetris-card-title">Landing Power</h3>
              {state.activePiece && (
                <span className="tetris-card-tag">{state.activePiece.type}-Tetromino</span>
              )}
            </div>

            {activePower ? (
              <div>
                <div className="tetris-power-header">
                  <h4 className="tetris-power-name">{activePower.name}</h4>
                  <span className="tetris-power-type-pill">{activePower.visual}</span>
                </div>
                <p className="tetris-power-desc">{activePower.description}</p>
                <p className="tetris-power-hint">⚡ Triggers automatically on landing & locking!</p>
              </div>
            ) : (
              <p className="tetris-power-desc" style={{ color: '#687d96' }}>
                Spawn a piece to inspect its landing power.
              </p>
            )}
          </section>

          {/* Key Bindings Quick Reference */}
          <section className="tetris-card">
            <div className="tetris-card-header">
              <h3 className="tetris-card-title">Controls</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11, color: '#9bb0c7' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Move Left / Right</span>
                <strong style={{ color: '#fff' }}>← / → or A / D</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Rotate CW / CCW</span>
                <strong style={{ color: '#fff' }}>↑ / Z</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Soft Drop / Hard</span>
                <strong style={{ color: '#fff' }}>↓ / Space</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Hold Piece</span>
                <strong style={{ color: '#fff' }}>C / Shift</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Pause / Restart</span>
                <strong style={{ color: '#fff' }}>P / R</strong>
              </div>
            </div>
          </section>
        </aside>

        {/* Center: The Neon Tetris Matrix */}
        <main className="tetris-center">
          <div
            className="tetris-matrix-container"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            tabIndex={0}
            aria-label="Tetris grid game board"
          >
            {/* Power Activation Banner */}
            {powerBanner && (
              <div
                className="tetris-banner-power"
                style={{ '--power-color': powerBanner.color } as CSSProperties}
              >
                {powerBanner.text}
              </div>
            )}

            {/* In-game floating combo indicator */}
            {state.combo > 1 && (
              <div className="tetris-combo-badge">
                <span className="tetris-combo-tag">COMBO ×{state.combo}</span>
                {state.backToBack && <span className="tetris-b2b-tag">BACK-TO-BACK</span>}
              </div>
            )}

            {/* 10x20 Grid Cells */}
            <div className="tetris-matrix">
              {Array.from({ length: BOARD_HEIGHT }, (_, y) =>
                Array.from({ length: BOARD_WIDTH }, (_, x) => {
                  const key = `${x},${y}`
                  const activeType = activeCellsMap.get(key)
                  const isGhost = !activeType && ghostCellsSet.has(key) && state.board[y]![x] === null
                  const boardType = state.board[y]![x]
                  const isEffect = effectCells.has(key)

                  const type = activeType || boardType

                  let cellClass = 'tetris-cell'
                  if (activeType) {
                    cellClass += ` piece-${activeType} is-active-piece`
                  } else if (boardType) {
                    cellClass += ` piece-${boardType}`
                  } else if (isGhost && state.activePiece) {
                    cellClass += ' is-ghost'
                  } else {
                    cellClass += ' is-empty'
                  }

                  if (isEffect) {
                    cellClass += ' is-effect'
                  }

                  const ghostColor = state.activePiece ? PIECE_COLORS[state.activePiece.type] : undefined

                  return (
                    <div
                      key={key}
                      className={cellClass}
                      style={
                        isGhost && ghostColor
                          ? ({ '--ghost-color': ghostColor } as CSSProperties)
                          : undefined
                      }
                    />
                  )
                })
              )}
            </div>

            {/* Overlays: Paused, Game Over, Won */}
            {state.status === 'paused' && (
              <div className="tetris-overlay">
                <span className="tetris-overlay-badge is-paused">Paused</span>
                <h3 className="tetris-overlay-title">Game Paused</h3>
                <p style={{ color: '#8fa5bf', fontSize: 13, margin: '0 0 20px 0' }}>
                  Press <kbd style={{ background: '#222', padding: '2px 6px', borderRadius: 4 }}>P</kbd> or Resume to continue.
                </p>
                <button
                  type="button"
                  className="tetris-btn-primary"
                  onClick={() => dispatch(tetrisActions.resume())}
                >
                  Resume
                </button>
                <button
                  type="button"
                  className="tetris-btn-secondary"
                  onClick={() => dispatch(tetrisActions.restart(state.mode))}
                >
                  Restart Game
                </button>
              </div>
            )}

            {state.status === 'game-over' && (
              <div className="tetris-overlay">
                <span className="tetris-overlay-badge is-gameover">Overload</span>
                <h3 className="tetris-overlay-title">Game Over</h3>
                <div className="tetris-overlay-stats">
                  <div className="tetris-stat-box">
                    <span>Score</span>
                    <strong>{state.score.toLocaleString()}</strong>
                  </div>
                  <div className="tetris-stat-box">
                    <span>Lines</span>
                    <strong>{state.lines}</strong>
                  </div>
                  <div className="tetris-stat-box">
                    <span>Powers</span>
                    <strong>{state.stats.powerActivations}</strong>
                  </div>
                  <div className="tetris-stat-box">
                    <span>Max Combo</span>
                    <strong>{state.stats.maxCombo}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  className="tetris-btn-primary"
                  onClick={() => dispatch(tetrisActions.restart(state.mode))}
                >
                  Play Again
                </button>
              </div>
            )}

            {state.status === 'won' && (
              <div className="tetris-overlay">
                <span className="tetris-overlay-badge is-victory">Goal Complete</span>
                <h3 className="tetris-overlay-title">Victory!</h3>
                <p style={{ color: '#62e6a7', fontSize: 13, margin: '0 0 12px 0' }}>
                  Target achieved in {formatDuration(state.elapsedMs)}!
                </p>
                <div className="tetris-overlay-stats">
                  <div className="tetris-stat-box">
                    <span>Score</span>
                    <strong>{state.score.toLocaleString()}</strong>
                  </div>
                  <div className="tetris-stat-box">
                    <span>Lines</span>
                    <strong>{state.lines}</strong>
                  </div>
                  <div className="tetris-stat-box">
                    <span>Powers</span>
                    <strong>{state.stats.powerActivations}</strong>
                  </div>
                  <div className="tetris-stat-box">
                    <span>Time</span>
                    <strong>{formatDuration(state.elapsedMs)}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  className="tetris-btn-primary"
                  onClick={() => dispatch(tetrisActions.restart(state.mode))}
                >
                  Play Again
                </button>
              </div>
            )}
          </div>
        </main>

        {/* Right Panel: Metrics, Goals & Next Queue */}
        <aside className="tetris-panel">
          {/* Score & High Score */}
          <section className="tetris-card">
            <div className="tetris-metric-grid">
              <div className="tetris-metric">
                <span className="tetris-metric-label">Score</span>
                <span className="tetris-metric-val is-score">{state.score.toLocaleString()}</span>
              </div>
              <div className="tetris-metric">
                <span className="tetris-metric-label">Best ({state.mode})</span>
                <span className="tetris-metric-val is-high">{state.highScore.toLocaleString()}</span>
              </div>
              <div className="tetris-metric">
                <span className="tetris-metric-label">Level</span>
                <span className="tetris-metric-val">{state.level}</span>
              </div>
              <div className="tetris-metric">
                <span className="tetris-metric-label">Lines</span>
                <span className="tetris-metric-val">{state.lines}</span>
              </div>
            </div>

            {/* Goal Progress Bar */}
            <div className="tetris-progress-container">
              <div className="tetris-progress-label">
                <span>{state.mode === 'sprint' ? 'Sprint 40 Goal' : state.mode === 'power-run' ? 'Target Score' : 'Next Level'}</span>
                <strong>{goalProgress.label}</strong>
              </div>
              <div className="tetris-progress-bar">
                <div className="tetris-progress-fill" style={{ width: `${goalProgress.percent}%` }} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#7b8fa7', marginTop: 10 }}>
              <span>Time: <strong style={{ color: '#ffffff' }}>{formatDuration(state.elapsedMs)}</strong></span>
              <span>Speed: <strong style={{ color: '#ffffff' }}>{state.dropInterval}ms</strong></span>
            </div>
          </section>

          {/* Next Queue Preview */}
          <section className="tetris-card">
            <div className="tetris-card-header">
              <h3 className="tetris-card-title">Next Pieces</h3>
              <span className="tetris-card-tag">7-Bag</span>
            </div>
            <div className="tetris-queue-list">
              {state.queue.slice(0, 4).map((type, idx) => (
                <div key={`${type}-${idx}`} className="tetris-queue-item">
                  <MiniPiece type={type} />
                </div>
              ))}
            </div>
          </section>

          {/* Battle / Power Stats */}
          <section className="tetris-card">
            <div className="tetris-card-header">
              <h3 className="tetris-card-title">Combat Stats</h3>
              <LightningIcon size={14} color="#ffd166" weight="fill" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11, color: '#8fa5bf' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Powers Activated</span>
                <strong style={{ color: '#5ee7ff' }}>{state.stats.powerActivations}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Power Cells Cleared</span>
                <strong style={{ color: '#62e6a7' }}>{state.stats.powerCellsCleared}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Max Combo</span>
                <strong style={{ color: '#ffd166' }}>{state.stats.maxCombo}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Tetrises (4-lines)</span>
                <strong style={{ color: '#c084fc' }}>{state.stats.tetrises}</strong>
              </div>
            </div>
          </section>
        </aside>
      </div>

      {/* On-Screen Touch Controls (Ergonomic Glass D-Pad & Action Buttons) */}
      {touchControlsVisible && (
        <div className="tetris-touch-controls" role="group" aria-label="Touch Controls">
          {/* D-Pad */}
          <div className="tetris-touch-dpad">
            <div />
            <button
              type="button"
              className="tetris-touch-btn"
              onClick={() => {
                dispatch(tetrisActions.rotateCW())
                tetrisAudio.playRotate()
              }}
              aria-label="Rotate Clockwise"
            >
              ↻
            </button>
            <div />
            <button
              type="button"
              className="tetris-touch-btn"
              onClick={() => {
                dispatch(tetrisActions.moveLeft())
                tetrisAudio.playMove()
              }}
              aria-label="Move Left"
            >
              ◀
            </button>
            <button
              type="button"
              className="tetris-touch-btn"
              onClick={() => {
                dispatch(tetrisActions.softDrop())
                tetrisAudio.playSoftDrop()
              }}
              aria-label="Soft Drop"
            >
              ▼
            </button>
            <button
              type="button"
              className="tetris-touch-btn"
              onClick={() => {
                dispatch(tetrisActions.moveRight())
                tetrisAudio.playMove()
              }}
              aria-label="Move Right"
            >
              ▶
            </button>
          </div>

          {/* Actions */}
          <div className="tetris-touch-actions">
            <button
              type="button"
              className="tetris-touch-btn btn-hold"
              onClick={() => {
                if (state.canHold) {
                  dispatch(tetrisActions.hold())
                  tetrisAudio.playHold()
                }
              }}
              aria-label="Hold Piece"
            >
              ⮂
            </button>
            <button
              type="button"
              className="tetris-touch-btn"
              onClick={() => {
                dispatch(tetrisActions.rotateCCW())
                tetrisAudio.playRotate()
              }}
              aria-label="Rotate Counter-Clockwise"
            >
              ↺
            </button>
            <button
              type="button"
              className="tetris-touch-btn btn-hard-drop"
              style={{ gridColumn: 'span 2' }}
              onClick={() => {
                dispatch(tetrisActions.hardDrop())
                tetrisAudio.playHardDrop()
              }}
              aria-label="Hard Drop"
            >
              ⤓ DROP
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
